import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CodeIcon from "@mui/icons-material/Code";
import { JsonEditor } from "json-edit-react";

type AnyObj = Record<string, any>;

const shortClass = (fq?: string) => {
  if (!fq || typeof fq !== "string") return fq;
  const parts = fq.split(".");
  return parts[parts.length - 1];
};

const KvChips: React.FC<{ data?: AnyObj; color?: any }> = ({
  data,
  color = "default",
}) => {
  if (!data || typeof data !== "object" || Object.keys(data).length === 0)
    return null;
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
      {Object.entries(data).map(([k, v]) => (
        <Chip
          key={k}
          size="small"
          variant="outlined"
          color={color}
          label={
            <Box component="span" sx={{ fontSize: "0.72rem" }}>
              <Box component="span" sx={{ color: "text.secondary" }}>
                {k}:
              </Box>{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {typeof v === "object" ? JSON.stringify(v) : String(v)}
              </Box>
            </Box>
          }
        />
      ))}
    </Stack>
  );
};

const ResourceCard: React.FC<{ name: string; spec: AnyObj; tint: string }> = ({
  name,
  spec,
  tint,
}) => {
  const tc = spec?.target_class;
  const kwargs = spec?.kwargs;
  const need = spec?.need;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, borderLeft: `3px solid ${tint}` }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: tc || kwargs || need ? 1 : 0, flexWrap: "wrap" }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
          {name}
        </Typography>
        {tc && (
          <Tooltip title={tc}>
            <Chip
              size="small"
              label={shortClass(tc)}
              sx={{
                fontFamily: "monospace",
                bgcolor: `${tint}1a`,
                color: tint,
                fontWeight: 700,
              }}
            />
          </Tooltip>
        )}
      </Stack>
      {(kwargs || need) && (
        <Stack spacing={1}>
          {kwargs && Object.keys(kwargs).length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 700 }}
              >
                kwargs
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <KvChips data={kwargs} color="primary" />
              </Box>
            </Box>
          )}
          {need && Object.keys(need).length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 700 }}
              >
                must match
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <KvChips data={need} color="secondary" />
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
};

const Section: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  tint: string;
  entries: [string, AnyObj][];
}> = ({ title, subtitle, icon, tint, entries }) => {
  if (entries.length === 0) return null;
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Box sx={{ color: tint, display: "flex" }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
          {title}
        </Typography>
        <Chip size="small" label={entries.length} sx={{ height: 20 }} />
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.5, fontSize: "0.8rem" }}
      >
        {subtitle}
      </Typography>
      <Stack spacing={1}>
        {entries.map(([name, spec]) => (
          <ResourceCard key={name} name={name} spec={spec || {}} tint={tint} />
        ))}
      </Stack>
    </Box>
  );
};

const ManifestViewer: React.FC<{ data?: AnyObj }> = ({ data }) => {
  const [showRaw, setShowRaw] = useState(false);

  // The manifest can be the object itself or wrapped under a `manifest` key.
  const manifest: AnyObj =
    data && typeof data === "object" && (data.create || data.find)
      ? data
      : data?.manifest || data || {};

  const createEntries = Object.entries(manifest.create || {}) as [
    string,
    AnyObj
  ][];
  const findEntries = Object.entries(manifest.find || {}) as [string, AnyObj][];
  const isStructured = createEntries.length > 0 || findEntries.length > 0;

  return (
    <Box>
      {isStructured ? (
        <Stack spacing={2.5} sx={{ mb: 2 }}>
          <Section
            title="Creates"
            subtitle="Devices this case instantiates in the test environment."
            icon={<AddCircleOutlineIcon fontSize="small" />}
            tint="#15803d"
            entries={createEntries}
          />
          {createEntries.length > 0 && findEntries.length > 0 && (
            <Divider />
          )}
          <Section
            title="Requires"
            subtitle="Resources that must already exist on an agent for it to pick up this case."
            icon={<TravelExploreIcon fontSize="small" />}
            tint="#E9672D"
            entries={findEntries}
          />
        </Stack>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2, fontSize: "0.85rem" }}>
          This case has no structured resource manifest.
        </Typography>
      )}

      <Button
        size="small"
        startIcon={<CodeIcon />}
        onClick={() => setShowRaw((s) => !s)}
        sx={{ color: "text.secondary" }}
      >
        {showRaw ? "Hide raw JSON" : "Show raw JSON"}
      </Button>
      <Collapse in={showRaw} unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.level1",
          }}
        >
          <JsonEditor
            rootName="manifest"
            minWidth="100%"
            data={manifest ?? {}}
            collapse={2}
            restrictAdd
            restrictDelete
            restrictEdit
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ManifestViewer;
